import { useRef } from "react";
import { Button, Icons } from "@/components/ui";
import { pickNativeFiles } from "@/desktop/pick-native-files";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { isTauri } from "@/lib/is-tauri";
import type { InputBarProps } from "./input-bar-types";

export function ComposerFilePicker({
  onFile,
  disabled,
}: {
  onFile?: InputBarProps["onImageFile"];
  disabled?: boolean;
}) {
  const t = useT(inputBarMessages);
  const input = useRef<HTMLInputElement>(null);
  const latest = useRef({ onFile, disabled });
  latest.current = { onFile, disabled };
  if (!onFile) return null;
  // The desktop app's own picker shows above the Workspace overlay.
  const pickFiles = () => {
    if (!isTauri()) {
      input.current?.click();
      return;
    }
    void pickNativeFiles({ multiple: true }).then((files) => {
      if (latest.current.disabled) return;
      files.forEach((file) => latest.current.onFile?.(file, "paste"));
    });
  };
  return (
    <>
      <input
        ref={input}
        type="file"
        disabled={disabled}
        multiple
        className="hidden"
        data-testid="input-bar-file-picker"
        onChange={(event) => {
          if (disabled) return;
          Array.from(event.target.files ?? []).forEach((file) =>
            onFile(file, "paste"),
          );
          event.target.value = "";
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        disabled={disabled}
        aria-label={t("attachFiles")}
        data-testid="input-bar-attach-file"
        onClick={pickFiles}
      >
        <Icons.Plus className="h-4 w-4" />
      </Button>
    </>
  );
}
